import { useTranslation } from "react-i18next";
import type { CampaignResultStatus, CampaignResultsSummary } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import {
  CAMPAIGN_RESULT_METRIC_CARDS,
  campaignResultMetricToneClass,
} from "./campaign-results-presentation.js";

export function CampaignResultsSummary({
  summary,
  resultStatus,
  onSelectFilter,
}: {
  summary: CampaignResultsSummary;
  resultStatus: CampaignResultStatus | "ALL";
  onSelectFilter: (status: CampaignResultStatus | "ALL") => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <div
      className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7"
      data-testid="campaign-results-summary"
      role="toolbar"
      aria-label={t("campaigns.results.title")}
    >
      {CAMPAIGN_RESULT_METRIC_CARDS.map((card) => {
        const selected = resultStatus === card.filter;
        const Icon = card.icon;
        const label = t(card.labelKey);
        const count = new Intl.NumberFormat(i18n.language).format(summary[card.key]);
        const excludedHint =
          card.key === "excluded" ? t("campaigns.results.excludedHint") : undefined;
        return (
          <button
            key={card.key}
            type="button"
            aria-pressed={selected}
            aria-label={`${label}: ${count}`}
            title={excludedHint}
            onClick={() => onSelectFilter(card.filter)}
            className={cn(
              "grid gap-1.5 rounded-lg border bg-surface px-2.5 py-2 text-start transition-colors",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/50",
              card.key === "total" ? "col-span-2 sm:col-span-1" : null,
              selected
                ? "border-accent/45 bg-accent/8 ring-1 ring-accent/25"
                : "border-line/70 hover:bg-surface-muted",
            )}
            data-testid={`campaign-results-metric-${card.key}`}
          >
            <span className="flex items-center gap-1.5 text-[0.7rem] font-medium text-muted">
              <span
                className={cn(
                  "inline-grid size-6 shrink-0 place-items-center rounded-full",
                  campaignResultMetricToneClass(card.tone),
                )}
                aria-hidden="true"
              >
                <Icon size={12} />
              </span>
              {label}
            </span>
            <span className="text-[1.125rem] font-semibold tabular-nums tracking-[-0.02em]">
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
